'use client'

import { useEffect, useState } from 'react'

import { getBrand } from '@/utils/brand.util'
import { captureException } from '@/utils/monitoring'
import { makeErrorId, reportClientError } from '@/utils/reportError'

type Props = {
  error: Error & { digest?: string }
  reset: () => void
}

const IS_PRODUCTION = process.env.NODE_ENV === 'production'

export default function GlobalError({ error }: Props) {
  const [errorId] = useState(makeErrorId)

  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : undefined
    captureException(error, {
      tags: {
        boundary: 'global-error',
        error_id: errorId,
        digest: error.digest ?? null,
        path: path ?? null,
      },
      extra: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
      },
    })
    reportClientError(error, { boundary: 'global-error', path, errorId })
  }, [error, errorId])

  return (
    <html>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#f6f4ef',
          color: '#1a1a1a',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>Oops! Something went wrong.</h1>
          <p style={{ fontSize: 15, lineHeight: 1.5, color: '#555', marginBottom: 8 }}>Your work is safe.</p>
          <p style={{ fontSize: 15, lineHeight: 1.5, color: '#555', marginBottom: 20 }}>
            Please refresh the page. If it still doesn&apos;t work, email{' '}
            <a href={`mailto:${getBrand().email}`} style={{ color: '#1a1a1a', textDecoration: 'underline' }}>
              {getBrand().email}
            </a>{' '}
            and include this support code:
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                cursor: 'pointer',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 15,
                fontWeight: 600,
                background: '#1a1a1a',
                color: '#fff',
              }}
            >
              Refresh Page
            </button>
          </div>

          <p style={{ fontSize: 13, color: '#8a8577', fontFamily: 'ui-monospace, monospace' }}>
            Support Code: {errorId}
          </p>

          {!IS_PRODUCTION && (
            <pre
              style={{
                marginTop: 20,
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                background: '#fff',
                border: '1px solid #e5e1d8',
                borderRadius: 8,
                padding: 16,
                fontSize: 12,
                lineHeight: 1.5,
                color: '#b00020',
                overflowX: 'auto',
              }}
            >
              {error.name}: {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          )}
        </div>
      </body>
    </html>
  )
}
